import React from 'react';
import { motion } from 'framer-motion';
import { FiCpu, FiTrendingUp, FiCheckCircle, FiArrowRight, FiZap, FiTarget, FiBarChart, FiActivity } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { imageConfig } from '../../config/images';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';

const AISEOPage = () => {
  useDocumentTitle(
    'AI Powered SEO Services in India | Smart Optimization With AIO',
    'Experience next level search visibility with our AI driven approach that blends AEO and an easy AI overview process. Simple, fast, and designed to grow your rankings with smarter automation.'
  );
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-br from-orange-600 via-red-600 to-orange-700">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnptMCAzNmMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnoiIHN0cm9rZT0iI2ZmZiIgc3Ryb2tlLW9wYWNpdHk9Ii4xIi8+PC9nPjwvc3ZnPg==')] opacity-10"></div>
        
        <div className="container mx-auto px-4 relative z-10 w-full">
          <div className="flex justify-center items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="max-w-5xl text-center"
            >
              <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
                <FiCpu className="text-white" />
                <span className="text-white font-semibold">Optimised for SEO, AIO, AEO & GEO</span>
              </div>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6">
                AI-Powered SEO, AIO & AEO Services
              </h1>
              <p className="text-xl md:text-2xl text-white/90 mb-8 leading-relaxed max-w-4xl mx-auto">
                Rank on Google, get cited in AI Overviews, and become the preferred answer for ChatGPT-style tools, Bing Copilot, and other answer engines with Mirani Marketing's AI-first SEO framework.
              </p>
              <div className="space-y-4 mb-8">
                <p className="text-lg text-white/80">Built for Google, AI Overviews, Perplexity, Copilot & more</p>
                <p className="text-lg text-white/80">Proven performance marketing team in India, serving global brands</p>
              </div>
              <div className="flex flex-wrap gap-4 justify-center">
                <Link
                  to="/contact"
                  className="inline-flex items-center space-x-2 bg-white text-orange-600 px-8 py-4 rounded-full font-semibold hover:bg-orange-500 hover:text-white transition-all duration-300 shadow-lg hover:shadow-2xl transform hover:scale-105"
                >
                  <span>Book a Free AI SEO Audit</span>
                  <FiArrowRight className="w-5 h-5" />
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center space-x-2 border-2 border-white text-white px-8 py-4 rounded-full font-semibold hover:bg-white hover:text-orange-600 transition-all duration-300"
                >
                  <span>See AI SEO Packages</span>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What is AI SEO */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-800 mb-8">What is AI-Powered SEO?</h2>
            <h3 className="text-2xl font-semibold text-orange-600 mb-6">AI SEO + AIO + AEO in one system</h3>
            <p className="text-xl text-gray-600 leading-relaxed mb-6">
              AI-powered SEO combines traditional SEO with Answer Engine Optimization (AEO), AI Overview (AIO) optimization, and Generative Engine Optimization (GEO) so your content can rank, get cited, and be recommended across search and LLMs.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            <div className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="bg-gradient-to-br from-orange-500 to-red-600 w-16 h-16 rounded-xl flex items-center justify-center mb-6">
                <FiActivity className="text-3xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">AI SEO</h3>
              <p className="text-gray-600 leading-relaxed">
                Use machine learning tools to research topics, cluster keywords, and optimise on-page and technical SEO faster.
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="bg-gradient-to-br from-orange-500 to-red-600 w-16 h-16 rounded-xl flex items-center justify-center mb-6">
                <FiZap className="text-3xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">AEO (Answer Engine Optimization)</h3>
              <p className="text-gray-600 leading-relaxed">
                Structure content so answer engines can lift your page as a clear, direct answer to user questions.
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="bg-gradient-to-br from-orange-500 to-red-600 w-16 h-16 rounded-xl flex items-center justify-center mb-6">
                <FiTarget className="text-3xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">AIO (AI Overview Optimization)</h3>
              <p className="text-gray-600 leading-relaxed">
                Format and enrich pages to be included and cited in Google's AI Overviews.
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="bg-gradient-to-br from-orange-500 to-red-600 w-16 h-16 rounded-xl flex items-center justify-center mb-6">
                <FiBarChart className="text-3xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">GEO (Generative Engine Optimization)</h3>
              <p className="text-gray-600 leading-relaxed">
                Optimise entities, brand mentions, and structure so LLMs repeatedly cite your site as a trusted source.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* AI SEO Features Showcase */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-800 mb-8">AI-Powered Features</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Advanced capabilities that give you an unfair advantage
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-2xl p-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
              <h3 className="text-xl font-bold text-gray-800 mb-3">AI Keyword & Topic Intelligence</h3>
              <p className="text-gray-600">
                Cluster-based, intent-driven keyword research for human searchers and conversational prompts, including questions people ask to AI tools.
              </p>
            </div>
            
            <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-2xl p-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
              <h3 className="text-xl font-bold text-gray-800 mb-3">AEO Content Architecture</h3>
              <p className="text-gray-600">
                Question-led site and blog structure with answer blocks, FAQs, and schema designed for snippets, AI Overviews, and LLM answers.
              </p>
            </div>
            
            <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-2xl p-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
              <h3 className="text-xl font-bold text-gray-800 mb-3">Technical AI SEO & Schema</h3>
              <p className="text-gray-600">
                Clean site architecture, page speed, structured data, and entity markup tailored to both classic crawlers and generative engines.
              </p>
            </div>
            
            <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-2xl p-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
              <h3 className="text-xl font-bold text-gray-800 mb-3">GEO & LLM Optimization</h3>
              <p className="text-gray-600">
                Content formatting, attribution-friendly layouts, and entity optimisation so your brand is easy for ChatGPT-style models to quote and link.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-800 mb-8">AI SEO Packages</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Choose the perfect plan to supercharge your SEO with AI
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[
              {
                name: 'Starter',
                subtitle: 'For Local & Early-Stage Brands',
                price: '₹15,000',
                duration: '/month',
                popular: false,
                description: 'Ideal for service businesses and local companies testing AI SEO and AEO for the first time.',
                features: [
                  'AI SEO audit (site + content + technical)',
                  '1 AI keyword/topic cluster per month',
                  '2 SEO + AEO-optimised blog posts or service pages per month',
                  'Core schema (FAQ / HowTo / LocalBusiness where relevant)',
                  'Basic AI Overview readiness check',
                  'Monthly performance report'
                ]
              },
              {
                name: 'Growth',
                subtitle: 'For Scaling D2C, SaaS & Service Brands',
                price: '₹40,000',
                duration: '/month',
                popular: true,
                description: 'Built for brands ready to dominate organic, AI Overviews, and answer engines in multiple markets.',
                features: [
                  'Everything in Starter, plus:',
                  '3–5 keyword/topic clusters per month',
                  '6–8 AI SEO + AEO pages/posts per month',
                  'Advanced schema and entity optimisation (FAQ, HowTo, Article, Product, Organization, Local)',
                  'AI Overview testing & monitoring for priority topics',
                  'GEO optimisation for ChatGPT-style and Copilot-style engines',
                  'Monthly strategy call and roadmap updates'
                ]
              },
              {
                name: 'Custom Enterprise',
                subtitle: 'Multi-location & Global Brands',
                price: 'Custom',
                duration: 'pricing',
                popular: false,
                description: 'Custom AI SEO, AIO, AEO and GEO strategy for enterprises with complex sites, multiple locations, or international targeting.',
                features: [
                  'Full-funnel AI SEO strategy and implementation',
                  'Country/region-specific AEO and local SEO',
                  'Content ops system with AI-assisted workflows and human editing',
                  'Custom dashboards tracking rankings, AI citations, and answer placements',
                  'Dedicated strategist and implementation team'
                ]
              }
            ].map((pkg, index) => (
              <div 
                key={index}
                className={`relative bg-white rounded-2xl shadow-lg p-8 transform hover:scale-105 transition-all duration-300 ${
                  pkg.popular ? 'ring-4 ring-orange-500 shadow-2xl' : ''
                }`}
              >
                {pkg.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-gradient-to-r from-orange-500 to-red-600 text-white px-6 py-2 rounded-full text-sm font-bold shadow-lg">
                      MOST POPULAR
                    </span>
                  </div>
                )}
                
                <h3 className="text-2xl font-bold text-gray-800 mb-2">{pkg.name}</h3>
                <p className="text-sm text-orange-600 font-semibold mb-4">{pkg.subtitle}</p>
                <div className="mb-4">
                  <span className="text-5xl font-bold text-gray-800">{pkg.price}</span>
                  <span className="text-gray-600 ml-2">{pkg.duration}</span>
                </div>
                <p className="text-gray-600 mb-6">{pkg.description}</p>
                
                <ul className="space-y-3 mb-8">
                  {pkg.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-start gap-3">
                      <FiCheckCircle className="text-orange-600 flex-shrink-0 mt-1" />
                      <span className="text-gray-600 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <Link
                  to="/contact"
                  className={`block w-full text-center py-4 rounded-lg font-semibold transition-all duration-300 ${
                    pkg.popular 
                      ? 'bg-orange-600 hover:bg-orange-700 text-white shadow-lg' 
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-800'
                  }`}
                >
                  {pkg.name === 'Starter' ? 'Get Starter Plan' : pkg.name === 'Growth' ? 'Scale with Growth Plan' : 'Request Custom Proposal'}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose AI SEO */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-800 mb-8">Why Choose AI-Powered SEO with Mirani Marketing?</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center group">
              <div className="bg-gradient-to-br from-orange-500 to-red-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 transform group-hover:scale-110 transition-all duration-300 shadow-lg group-hover:shadow-2xl">
                <FiZap className="text-4xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Performance-first mindset</h3>
              <p className="text-gray-600 leading-relaxed">
                Mirani Marketing is a performance-driven agency, so AI SEO and AEO are always tied to leads, sales, and revenue—not just rankings.
              </p>
            </div>
            <div className="text-center group">
              <div className="bg-gradient-to-br from-orange-500 to-red-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 transform group-hover:scale-110 transition-all duration-300 shadow-lg group-hover:shadow-2xl">
                <FiActivity className="text-4xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Global yet local</h3>
              <p className="text-gray-600 leading-relaxed">
                Based in India, serving brands across India, US, UK, Canada, and Australia with localisation for each market and search landscape.
              </p>
            </div>
            <div className="text-center group">
              <div className="bg-gradient-to-br from-orange-500 to-red-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 transform group-hover:scale-110 transition-all duration-300 shadow-lg group-hover:shadow-2xl">
                <FiTrendingUp className="text-4xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Future-ready search strategy</h3>
              <p className="text-gray-600 leading-relaxed">
                Your content is designed for Google, AI Overviews, Bing Copilot, Perplexity and other answer engines, ensuring visibility where your customers actually get answers.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-800 mb-8">Frequently Asked Questions</h2>
          </div>

          <div className="max-w-4xl mx-auto space-y-6">
            {[
              {
                question: "What is Answer Engine Optimization (AEO)?",
                answer: "AEO is the practice of optimising your website so it appears as the direct answer inside AI-powered answer engines like Perplexity, Bing Copilot, Brave Search, and voice assistants like Alexa or Google Assistant. AEO focuses on structured data, concise answer boxes, and entity relationships."
              },
              {
                question: "How is AI SEO different from traditional SEO?",
                answer: "Traditional SEO optimises for the 10 blue links in Google. AI SEO optimises for search engines, AI Overviews, answer engines, and LLMs like ChatGPT. It combines traditional ranking signals with conversational language models, entity optimisation, semantic schemas, and source-worthy content design."
              },
              {
                question: "How do you optimise for AI Overviews (AIO)?",
                answer: "Google AI Overviews pull from pages that already rank well organically. We optimise by aligning your content with the patterns Google prefers—comparison tables, step-by-step guides, clear definitions, cited sources, images, and topical depth. Plus, we track AIO-triggered keywords and optimise specifically for them."
              },
              {
                question: "What is GEO in SEO?",
                answer: "GEO stands for Generative Engine Optimization, which means optimising your brand and content to appear as a cited source in LLM responses from tools like ChatGPT, Claude, Gemini, and Perplexity. GEO focuses on brand authority, citation-worthy formats, and entity presence in knowledge graphs."
              },
              {
                question: "How long does AI SEO take to show results?",
                answer: "Most clients start seeing visibility improvements within 60 to 90 days. AI SEO accelerates traditional timelines by focusing on high-impact topics, semantic authority, and answer engine placement—but like all SEO, sustained results take time and consistency."
              }
            ].map((faq, index) => (
              <details key={index} className="bg-white rounded-xl shadow-lg p-6 group hover:shadow-2xl transition-all duration-300">
                <summary className="text-xl font-bold text-gray-800 cursor-pointer flex items-center justify-between group-hover:text-orange-600 transition-colors">
                  {faq.question}
                  <FiArrowRight className="transform group-open:rotate-90 transition-transform text-orange-600" />
                </summary>
                <p className="mt-4 text-gray-600 leading-relaxed">
                  {faq.answer}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-orange-600 to-red-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-5xl font-bold mb-8">Ready to Experience AI-Powered SEO, AIO, AEO & GEO?</h2>
          <p className="text-2xl mb-12 text-white/90 max-w-3xl mx-auto">
            Turn your website into the answer LLMs and search engines trust.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center space-x-2 bg-white text-orange-600 px-8 py-4 rounded-full font-semibold hover:bg-orange-500 hover:text-white transition-all duration-300 shadow-lg hover:shadow-2xl transform hover:scale-105"
            >
              <span>Book a Free AI SEO & AEO Audit</span>
              <FiArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center space-x-2 border-2 border-white text-white px-8 py-4 rounded-full font-semibold hover:bg-white hover:text-orange-600 transition-all duration-300"
            >
              <span>Talk to Mirani Marketing</span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AISEOPage;
